import { StyleResolution } from '../../types/common';
import React from 'react';
import { processStyleDefinition, processStyleValueWithFunction } from '../../util/styleProcessor';
import { styleProperties, styleDefaults } from './promptStyleProperties';
import { ATTACH_SOURCE_MENU_CSS } from './components/AttachSourceMenu';

const PREFIX = '.comp.compPrompt';

export default function PromptStyle({
	theme,
}: Readonly<{ theme: Map<string, Map<string, string>> }>) {
	// Theme value for this component's own chrome, falling back to the literal it
	// replaced. The fallback is what makes this safe on a theme that predates the
	// variable: an absent value renders exactly as the hardcoded CSS did. Resolved
	// through processStyleValueWithFunction so a theme value that is itself a
	// `<var>` reference still resolves.
	const all = theme.get(StyleResolution.ALL) ?? new Map<string, string>();
	const t = (variable: string, fallback: string) =>
		all.get(variable) ? processStyleValueWithFunction(`<${variable}>`, all) : fallback;

	const css =
		`
		${PREFIX} {
			display: flex;
			flex-direction: row;
			height: 100%;
			overflow: hidden;
			background: ${t('colorSeven', '#fff')};
			font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
		}

		/* ─── Session Sidebar ─── */
		${PREFIX} ._sessionSidebar {
			width: 260px;
			height: 100%;
			background: ${t('surfaceColorOne', '#f9f9f9')};
			border-right: 1px solid ${t('borderColorNine', '#e5e5e5')};
			display: flex;
			flex-direction: column;
			flex-shrink: 0;
			position: relative;
			overflow: hidden;
			transition: width 0.2s ease, margin-left 0.2s ease, opacity 0.2s ease;
		}

		/* Sidebar resize handle */
		${PREFIX} ._sidebarResizeHandle {
			position: absolute;
			right: 0;
			top: 0;
			bottom: 0;
			width: 4px;
			cursor: col-resize;
			background: transparent;
			z-index: 5;
			transition: background 0.15s;
		}

		${PREFIX} ._sidebarResizeHandle:hover,
		${PREFIX} ._sidebarResizeHandle:active {
			background: ${t('surfaceColorThree', '#d0d0d0')};
		}

		${PREFIX} ._sessionSidebar._collapsed {
			width: 0 !important;
			margin-left: 0;
			border-right: none;
			opacity: 0;
			pointer-events: none;
		}

		${PREFIX} ._sidebarHeader {
			display: flex;
			align-items: center;
			justify-content: space-between;
			padding: 12px 14px;
			flex-shrink: 0;
			position: relative;
		}

		${PREFIX} ._newChatButton {
			display: flex;
			align-items: center;
			gap: 8px;
			border: none;
			background: transparent;
			color: ${t('promptFontColor', '#1a1a1a')};
			font-size: 14px;
			font-weight: 500;
			cursor: pointer;
			padding: 8px 12px;
			border-radius: 8px;
			transition: background 0.15s;
			position: relative;
		}

		${PREFIX} ._newChatButton:hover {
			background: ${t('surfaceColorThree', '#ececec')};
		}

		${PREFIX} ._newChatButton i {
			font-size: 12px;
		}

		${PREFIX} ._sessionGroup {
			flex: 1;
			overflow-y: auto;
			padding: 0 8px;
		}

		${PREFIX} ._sessionGroupLabel {
			font-size: 11px;
			font-weight: 600;
			color: ${t('promptTertiaryFontColor', '#9b9b9b')};
			text-transform: uppercase;
			letter-spacing: 0.5px;
			padding: 8px 8px 6px;
		}

		${PREFIX} ._sessionItems {
			display: flex;
			flex-direction: column;
			gap: 1px;
		}

		${PREFIX} ._sessionItem {
			display: flex;
			align-items: center;
			gap: 4px;
			width: 100%;
			text-align: left;
			border: none;
			background: transparent;
			color: ${t('promptFontColor', '#1a1a1a')};
			font-size: 14px;
			padding: 10px 12px;
			border-radius: 8px;
			cursor: pointer;
			transition: background 0.15s;
			overflow: hidden;
			position: relative;
		}

		${PREFIX} ._sessionItem:hover {
			background: ${t('surfaceColorThree', '#ececec')};
		}

		${PREFIX} ._sessionItem._active {
			background: ${t('surfaceColorThree', '#ececec')};
			font-weight: 500;
		}

		${PREFIX} ._sessionTitle {
			display: block;
			flex: 1 1 auto;
			min-width: 0;
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
		}

		${PREFIX} ._loadMoreSessions {
			display: block;
			width: 100%;
			text-align: center;
			border: none;
			background: transparent;
			color: ${t('promptSecondaryFontColor', '#6b6b6b')};
			font-size: 13px;
			padding: 10px 12px;
			cursor: pointer;
			transition: background 0.15s, color 0.15s;
			border-radius: 8px;
		}

		${PREFIX} ._loadMoreSessions:hover {
			background: ${t('surfaceColorThree', '#ececec')};
			color: ${t('promptFontColor', '#1a1a1a')};
		}

		${PREFIX} ._loadMoreSessions:disabled {
			opacity: 0.5;
			cursor: not-allowed;
		}

		/* Session action buttons (visible on hover). They sit in the row's flex
		   line, not over it, so the title ellipsis ends before them. */
		${PREFIX} ._sessionActions {
			flex: 0 0 auto;
			display: none;
			align-items: center;
			gap: 2px;
			margin: -4px -4px -4px 0;
		}

		${PREFIX} ._sessionItem:hover ._sessionActions {
			display: flex;
		}

		${PREFIX} ._renameSessionButton,
		${PREFIX} ._deleteSessionButton {
			width: 24px;
			height: 24px;
			border: none;
			background: transparent;
			color: ${t('promptTertiaryFontColor', '#9b9b9b')};
			cursor: pointer;
			border-radius: 4px;
			display: flex;
			align-items: center;
			justify-content: center;
			font-size: 11px;
			padding: 0;
			transition: background 0.15s, color 0.15s;
		}

		${PREFIX} ._renameSessionButton:hover {
			background: ${t('surfaceColorThree', '#ddd')};
			color: ${t('promptFontColor', '#1a1a1a')};
		}

		${PREFIX} ._deleteSessionButton:hover {
			background: ${t('surfaceColorThree', '#ddd')};
			color: ${t('colorTwelve', '#dc3545')};
		}

		/* In-place rename input */
		${PREFIX} ._sessionRenameInput {
			flex: 1 1 auto;
			min-width: 0;
			width: 100%;
			border: 1px solid ${t('borderColorTen', '#d0d0d0')};
			border-radius: 4px;
			padding: 4px 8px;
			font-size: 14px;
			font-family: inherit;
			color: ${t('promptFontColor', '#1a1a1a')};
			background: ${t('colorSeven', '#fff')};
			outline: none;
		}

		${PREFIX} ._sessionRenameInput:focus {
			border-color: ${t('borderColorEleven', '#999')};
		}

		/* Delete confirmation dialog */
		${PREFIX} ._deleteConfirmOverlay {
			position: fixed;
			inset: 0;
			z-index: 100;
			background: rgba(0, 0, 0, 0.4);
			display: flex;
			align-items: center;
			justify-content: center;
		}

		${PREFIX} ._deleteConfirmDialog {
			background: ${t('colorSeven', '#fff')};
			border-radius: 12px;
			padding: 20px 24px;
			max-width: 340px;
			width: 90%;
			box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
		}

		${PREFIX} ._deleteConfirmText {
			font-size: 14px;
			color: ${t('promptFontColor', '#1a1a1a')};
			margin: 0 0 16px;
			line-height: 1.5;
		}

		${PREFIX} ._deleteConfirmActions {
			display: flex;
			justify-content: flex-end;
			gap: 8px;
		}

		${PREFIX} ._deleteConfirmCancel,
		${PREFIX} ._deleteConfirmDelete {
			padding: 8px 16px;
			border-radius: 8px;
			font-size: 13px;
			font-weight: 500;
			cursor: pointer;
			border: none;
			transition: background 0.15s;
		}

		${PREFIX} ._deleteConfirmCancel {
			background: ${t('surfaceColorTwo', '#f4f4f4')};
			color: ${t('promptFontColor', '#1a1a1a')};
		}

		${PREFIX} ._deleteConfirmCancel:hover {
			background: ${t('surfaceColorThree', '#e5e5e5')};
		}

		${PREFIX} ._deleteConfirmDelete {
			background: ${t('colorTwelve', '#dc3545')};
			color: ${t('colorSeven', '#fff')};
		}

		${PREFIX} ._deleteConfirmDelete:hover {
			background: ${t('colorTwelve', '#c82333')};
		}

		${PREFIX} ._sidebarOverlay {
			display: none;
		}

		/* Overlay sessions: the list floats over the chat instead of sitting beside
		   it. Driven by a class (the component measures its own width, see
		   sessionsMode) and, as a fallback, by a genuinely small viewport. Both
		   selectors carry identical rules. */
		${PREFIX}._overlaySessions ._sessionSidebar {
			position: absolute;
			z-index: 20;
			width: 260px;
			transform: translateX(-100%);
			transition: transform 0.2s ease;
			box-shadow: 2px 0 8px rgba(0, 0, 0, 0.1);
			opacity: 1;
			pointer-events: auto;
		}

		${PREFIX}._overlaySessions ._sessionSidebar._collapsed {
			width: 260px;
			margin-left: 0;
			border-right: 1px solid ${t('borderColorNine', '#e5e5e5')};
			opacity: 1;
			pointer-events: auto;
			transform: translateX(-100%);
		}

		${PREFIX}._overlaySessions ._sessionSidebar._open {
			transform: translateX(0);
		}

		${PREFIX}._overlaySessions ._sidebarOverlay {
			display: block;
			position: absolute;
			inset: 0;
			z-index: 19;
			background: rgba(0, 0, 0, 0.3);
		}

		@media (max-width: 768px) {
			${PREFIX} ._sessionSidebar {
				position: absolute;
				z-index: 20;
				width: 260px;
				transform: translateX(-100%);
				transition: transform 0.2s ease;
				box-shadow: 2px 0 8px rgba(0, 0, 0, 0.1);
				opacity: 1;
				pointer-events: auto;
			}

			${PREFIX} ._sessionSidebar._collapsed {
				width: 260px;
				margin-left: 0;
				border-right: 1px solid ${t('borderColorNine', '#e5e5e5')};
				opacity: 1;
				pointer-events: auto;
				transform: translateX(-100%);
			}

			${PREFIX} ._sessionSidebar._open {
				transform: translateX(0);
			}

			${PREFIX} ._sidebarOverlay {
				display: block;
				position: absolute;
				inset: 0;
				z-index: 19;
				background: rgba(0, 0, 0, 0.3);
			}
		}

		/* ─── Main area ─── */
		${PREFIX} ._promptMain {
			flex: 1;
			display: flex;
			flex-direction: column;
			min-width: 0;
			overflow: hidden;
			position: relative;
		}

		/* Top bar */
		${PREFIX} ._promptTopBar {
			display: flex;
			align-items: center;
			justify-content: space-between;
			padding: 8px 12px;
			flex-shrink: 0;
		}

		/*
		 * The right-hand group. Its own container with margin-left auto rather than
		 * relying on the bar's space-between: that spreads whatever children there
		 * happen to be, so a third button would sit in the middle, and with the
		 * sessions toggle hidden a lone button would sit on the left.
		 */
		${PREFIX} ._promptTopRight {
			display: flex;
			align-items: center;
			gap: 2px;
			margin-left: auto;
		}

		${PREFIX} ._sidebarToggle,
		${PREFIX} ._newChatTopButton,
		${PREFIX} ._promptPreviewOpenButton,
		${PREFIX} ._promptOpenFullButton {
			width: 36px;
			height: 36px;
			border: none;
			background: transparent;
			color: ${t('promptSecondaryFontColor', '#6b6b6b')};
			cursor: pointer;
			border-radius: 8px;
			display: flex;
			align-items: center;
			justify-content: center;
			font-size: 16px;
			padding: 0;
			transition: background 0.15s, color 0.15s;
			/* It is an anchor, so it also carries link styling to undo. */
			text-decoration: none;
			flex-shrink: 0;
		}

		${PREFIX} ._sidebarToggle:hover,
		${PREFIX} ._newChatTopButton:hover,
		${PREFIX} ._promptPreviewOpenButton:hover,
		${PREFIX} ._promptOpenFullButton:hover {
			background: ${t('surfaceColorTwo', '#f4f4f4')};
			color: ${t('promptFontColor', '#1a1a1a')};
		}

		/* ─── Page preview ─── */
		/*
		 * A sibling of the chat column, not an overlay: the whole point is to see
		 * both at once. Width comes from the host as an inline style, so this only
		 * has to stop it growing or shrinking on its own.
		 */
		${PREFIX} ._promptPreviewPane {
			position: relative;
			height: 100%;
			flex-shrink: 0;
			display: flex;
			border-left: 1px solid ${t('borderColorNine', '#e5e5e5')};
			background: ${t('surfaceColorOne', '#f9f9f9')};
			min-width: 0;
		}

		${PREFIX} ._promptPreviewResizer {
			position: absolute;
			left: -3px;
			top: 0;
			width: 6px;
			height: 100%;
			border: none;
			padding: 0;
			background: transparent;
			cursor: col-resize;
			z-index: 2;
		}

		${PREFIX} ._promptPreviewResizer:hover {
			background: ${t('borderColorNine', '#e5e5e5')};
		}

		${PREFIX} ._promptPreview {
			display: flex;
			flex-direction: column;
			width: 100%;
			min-width: 0;
			height: 100%;
		}

		${PREFIX} ._promptPreviewBar {
			display: flex;
			align-items: center;
			gap: 8px;
			padding: 8px 10px;
			flex-shrink: 0;
			border-bottom: 1px solid ${t('borderColorNine', '#e5e5e5')};
		}

		${PREFIX} ._promptPreviewSurface {
			display: flex;
			gap: 2px;
			padding: 2px;
			border-radius: 8px;
			background: ${t('surfaceColorTwo', '#f4f4f4')};
			flex-shrink: 0;
		}

		${PREFIX} ._promptPreviewTab {
			border: none;
			background: transparent;
			color: ${t('promptSecondaryFontColor', '#6b6b6b')};
			font-size: 12px;
			padding: 4px 10px;
			border-radius: 6px;
			cursor: pointer;
		}

		${PREFIX} ._promptPreviewTab._active {
			background: ${t('colorSeven', '#fff')};
			color: ${t('promptFontColor', '#1a1a1a')};
			font-weight: 600;
		}

		/* The page being shown, and the box it can be changed in. Reads as text
		   until it is hovered or focused, because most of the time it is only
		   reporting where the pane is. Truncates rather than wrapping: it shares
		   one line with two button groups that must not move as the name changes. */
		${PREFIX} ._promptPreviewPath {
			flex: 1;
			min-width: 0;
			font-size: 12px;
			font-family: inherit;
			color: ${t('promptSecondaryFontColor', '#6b6b6b')};
			white-space: nowrap;
			overflow: hidden;
			text-overflow: ellipsis;
			border: 1px solid transparent;
			border-radius: 6px;
			background: transparent;
			padding: 4px 6px;
			outline: none;
		}

		${PREFIX} ._promptPreviewPath._pinned {
			color: ${t('promptFontColor', '#1a1a1a')};
		}

		${PREFIX} ._promptPreviewPath:hover {
			border-color: ${t('borderColorNine', '#e5e5e5')};
		}

		${PREFIX} ._promptPreviewPath:focus {
			border-color: ${t('borderColorNine', '#e5e5e5')};
			background: ${t('colorSeven', '#fff')};
			color: ${t('promptFontColor', '#1a1a1a')};
			text-overflow: clip;
		}

		${PREFIX} ._promptPreviewTools {
			display: flex;
			align-items: center;
			gap: 2px;
			flex-shrink: 0;
		}

		${PREFIX} ._promptPreviewTool {
			width: 28px;
			height: 28px;
			border: none;
			background: transparent;
			color: ${t('promptSecondaryFontColor', '#6b6b6b')};
			cursor: pointer;
			border-radius: 6px;
			display: flex;
			align-items: center;
			justify-content: center;
			font-size: 13px;
			padding: 0;
			text-decoration: none;
		}

		${PREFIX} ._promptPreviewTool:hover,
		${PREFIX} ._promptPreviewTool._active {
			background: ${t('surfaceColorTwo', '#f4f4f4')};
			color: ${t('promptFontColor', '#1a1a1a')};
		}

		/* Centres a narrowed device frame and lets a wide one scroll rather than
		   squeezing the iframe, which would misreport the viewport to the page. */
		${PREFIX} ._promptPreviewBody {
			flex: 1;
			min-height: 0;
			display: flex;
			justify-content: center;
			overflow: auto;
			background: ${t('surfaceColorTwo', '#f4f4f4')};
		}

		${PREFIX} ._promptPreviewFrame {
			width: 100%;
			height: 100%;
			border: none;
			background: ${t('colorSeven', '#fff')};
		}

		${PREFIX} ._promptPreviewEmpty {
			margin: auto;
			padding: 24px;
			text-align: center;
			font-size: 13px;
			color: ${t('promptSecondaryFontColor', '#6b6b6b')};
		}

		/* The offer, above the composer with the other post-turn notices. */
		${PREFIX} ._promptPreviewOffer {
			display: flex;
			align-items: center;
			gap: 8px;
			margin: 0 12px 8px;
			padding: 8px 12px;
			border-radius: 8px;
			font-size: 12px;
			background: ${t('surfaceColorTwo', '#f4f4f4')};
			color: ${t('promptSecondaryFontColor', '#6b6b6b')};
		}

		${PREFIX} ._promptPreviewOfferGo {
			margin-left: auto;
			flex-shrink: 0;
			border: none;
			border-radius: 6px;
			padding: 5px 10px;
			font-size: 12px;
			font-weight: 600;
			cursor: pointer;
			background: ${t('promptFontColor', '#1a1a1a')};
			color: ${t('colorSeven', '#fff')};
		}

		/* ─── Messages area ─── */
		${PREFIX} ._promptMessages {
			flex: 1;
			overflow-y: auto;
			overflow-x: hidden;
			position: relative;
		}

		${PREFIX} ._promptMessagesInner {
			max-width: 768px;
			margin: 0 auto;
			padding: 24px 16px;
			display: flex;
			flex-direction: column;
			gap: 24px;
		}


		${PREFIX} ._promptEmpty {
			display: flex;
			flex-direction: column;
		}

		${PREFIX} ._promptEmpty ._promptMessages {
			flex: none;
			margin-top: auto;
			display: flex;
			align-items: center;
			justify-content: center;
			overflow: visible;
		}

		${PREFIX} ._promptEmpty ._promptInputWrapper:last-child {
			margin-bottom: auto;
		}

		${PREFIX} ._promptEmpty ._quickActions {
			margin-bottom: auto;
		}


		${PREFIX} ._emptyState {
			display: flex;
			flex-direction: column;
			align-items: center;
			justify-content: center;
			padding: 0 16px 16px;
			width: 100%;
			animation: _promptFadeIn 0.4s ease;
		}

		@keyframes _promptFadeIn {
			from { opacity: 0; transform: translateY(10px); }
			to { opacity: 1; transform: translateY(0); }
		}

		${PREFIX} ._emptyTitle {
			font-size: 24px;
			font-weight: 600;
			color: ${t('promptFontColor', '#1a1a1a')};
			margin: 0;
			line-height: 1.4;
			text-align: center;
		}

		/* Quick actions */
		${PREFIX} ._quickActions {
			display: flex;
			flex-direction: column;
			gap: 8px;
			width: 100%;
			max-width: 600px;
			margin: 16px auto 0;
			max-height: 40vh;
			overflow-y: auto;
		}

		${PREFIX} ._quickActions._pills {
			flex-direction: row;
			flex-wrap: wrap;
			justify-content: center;
			max-width: 600px;
		}

		${PREFIX} ._quickActions._grid {
			display: grid;
			grid-template-columns: 1fr 1fr;
			gap: 12px;
			max-width: 520px;
		}

		${PREFIX} ._quickActionItem {
			display: flex;
			align-items: center;
			gap: 12px;
			padding: 12px 16px;
			border: 1px solid ${t('borderColorNine', '#e0e0e0')};
			border-radius: 12px;
			background: transparent;
			cursor: pointer;
			font-size: 14px;
			color: ${t('promptFontColor', '#1a1a1a')};
			text-align: left;
			transition: background 0.15s, border-color 0.15s;
		}

		${PREFIX} ._quickActionItem:hover:not(:disabled) {
			background: ${t('surfaceColorTwo', '#f5f5f5')};
			border-color: ${t('borderColorTen', '#c0c0c0')};
		}

		${PREFIX} ._quickActions._pills ._quickActionItem {
			padding: 8px 16px;
			border-radius: 20px;
			font-size: 13px;
		}

		${PREFIX} ._quickActionDisabled {
			opacity: 0.5;
			cursor: default;
		}

		${PREFIX} ._quickActionIcon {
			font-size: 16px;
			color: ${t('promptSecondaryFontColor', '#6b6b6b')};
			width: 20px;
			text-align: center;
			flex-shrink: 0;
		}

		${PREFIX} ._quickActionLabel {
			flex: 1;
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
		}

		${PREFIX} ._quickActionBadge {
			font-size: 11px;
			color: ${t('promptTertiaryFontColor', '#999')};
			background: ${t('surfaceColorTwo', '#f0f0f0')};
			padding: 2px 8px;
			border-radius: 10px;
			flex-shrink: 0;
		}

		${PREFIX} ._loadEarlierMessages {
			display: block;
			width: 100%;
			text-align: center;
			border: none;
			background: transparent;
			color: ${t('promptSecondaryFontColor', '#6b6b6b')};
			font-size: 13px;
			padding: 10px 12px;
			cursor: pointer;
			transition: background 0.15s, color 0.15s;
			border-radius: 8px;
			margin-bottom: 8px;
		}

		${PREFIX} ._loadEarlierMessages:hover {
			background: ${t('surfaceColorTwo', '#f4f4f4')};
			color: ${t('promptFontColor', '#1a1a1a')};
		}

		${PREFIX} ._loadEarlierMessages:disabled {
			opacity: 0.5;
			cursor: not-allowed;
		}

		/* ─── User message ─── */
		${PREFIX} ._promptMessage._user {
			align-self: flex-end;
			max-width: 75%;
			padding: 10px 16px;
			background: ${t('surfaceColorTwo', '#f4f4f4')};
			color: ${t('promptFontColor', '#1a1a1a')};
			border-radius: 20px;
			font-size: 15px;
			line-height: 1.6;
			word-wrap: break-word;
			/* The bubble is a plain <span>; without pre-wrap HTML collapses the
			   user's typed newlines into spaces (a formatted competitor LIST
			   rendered as one paragraph, live 2026-09-11). Data was never lost -
			   display only. */
			white-space: pre-wrap;
			position: relative;
		}

		/*
		 * A steer: sent into a turn that is already running, and not yet
		 * acknowledged by the agent. Faded and dashed because it is not part of
		 * the conversation until the agent says it read it, at which point the
		 * class comes off and it looks like any other message.
		 */
		${PREFIX} ._promptMessage._user._pending {
			opacity: 0.6;
			border: 1px dashed ${t('fontColorThree', '#9b9b9b')};
			background: transparent;
		}

		${PREFIX} ._promptMessage._user._pending ._pendingNote {
			display: block;
			margin-top: 4px;
			font-size: 12px;
			color: ${t('fontColorThree', '#9b9b9b')};
			white-space: normal;
		}

		/* ─── Assistant message ─── */
		${PREFIX} ._promptMessage._assistant {
			display: flex;
			align-items: flex-start;
			gap: 12px;
			align-self: flex-start;
			width: 100%;
			position: relative;
		}

		${PREFIX} ._assistantContent {
			flex: 1;
			min-width: 0;
			font-size: 15px;
			line-height: 1.7;
			color: ${t('promptFontColor', '#1a1a1a')};
		}

		${PREFIX} ._assistantContent p {
			margin: 0 0 12px 0;
		}

		${PREFIX} ._assistantContent p:last-child {
			margin-bottom: 0;
		}

		${PREFIX} ._assistantContent code {
			background: ${t('surfaceColorTwo', '#f4f4f4')};
			padding: 2px 5px;
			border-radius: 4px;
			font-size: 0.88em;
			font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
		}

		${PREFIX} ._assistantContent pre {
			background: ${t('promptFontColor', '#1e1e1e')};
			color: ${t('fontColorTen', '#d4d4d4')};
			padding: 16px;
			border-radius: 8px;
			overflow-x: auto;
			margin: 12px 0;
			font-size: 13px;
			line-height: 1.5;
		}

		${PREFIX} ._assistantContent pre code {
			background: none;
			padding: 0;
			color: inherit;
			font-size: inherit;
		}

		${PREFIX} ._assistantContent ul,
		${PREFIX} ._assistantContent ol {
			padding-left: 20px;
			margin: 8px 0;
		}

		${PREFIX} ._assistantContent li {
			margin-bottom: 4px;
		}

		/* ─── Thinking block ─── */
		${PREFIX} ._thinkingBlock {
			/* Never wider than the chat column: as a flex-start item it sizes to its
			   content, and a long tool summary pushed it past a narrow panel's edge. */
			max-width: min(768px, 100%);
			min-width: 0;
			border-radius: 12px;
			font-size: 13px;
			margin-bottom: 4px;
			align-self: flex-start;
		}

		${PREFIX} ._thinkingHeader {
			display: flex;
			align-items: center;
			gap: 10px;
			padding: 8px 12px;
			border: none;
			background: none;
			font: inherit;
			color: inherit;
			width: 100%;
			text-align: left;
			cursor: pointer;
			border-radius: 8px;
		}

		${PREFIX} ._thinkingHeader:hover {
			background: ${t('surfaceColorTwo', '#f4f4f4')};
		}

		${PREFIX} ._thinkingHeader i,
		${PREFIX} ._thinkingChevron {
			font-size: 10px;
			color: ${t('promptTertiaryFontColor', '#9b9b9b')};
		}

		${PREFIX} ._thinkingChevron {
			margin-left: auto;
		}

		${PREFIX} ._thinkingDots {
			display: flex;
			gap: 4px;
		}

		${PREFIX} ._thinkingDots span {
			width: 6px;
			height: 6px;
			border-radius: 50%;
			background: ${t('promptTertiaryFontColor', '#8b8b8b')};
			animation: promptThinkingBounce 1.4s ease-in-out infinite;
		}

		${PREFIX} ._thinkingDots span:nth-child(2) {
			animation-delay: 0.2s;
		}

		${PREFIX} ._thinkingDots span:nth-child(3) {
			animation-delay: 0.4s;
		}

		@keyframes promptThinkingBounce {
			0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); }
			40% { opacity: 1; transform: scale(1); }
		}

		${PREFIX} ._thinkingLabel {
			font-size: 13px;
			color: ${t('promptTertiaryFontColor', '#8b8b8b')};
			font-style: italic;
		}

		${PREFIX} ._thinkingBody {
			padding: 0 0 8px 20px;
			display: flex;
			flex-direction: column;
			gap: 4px;
			/* What is still wider than the column (a tool's data, a long line of
			   reasoning) scrolls here, inside the expanded tools, and nowhere else. */
			overflow-x: auto;
		}

		${PREFIX} ._thinkingToolEntry {
			display: flex;
			flex-direction: column;
		}

		${PREFIX} ._thinkingToolRow {
			display: flex;
			align-items: center;
			gap: 8px;
			padding: 4px 8px;
			font-size: 12px;
			color: ${t('promptSecondaryFontColor', '#666')};
			border: none;
			background: none;
			font: inherit;
			text-align: left;
			width: 100%;
			border-radius: 6px;
		}

		${PREFIX} ._thinkingToolRow._clickable {
			cursor: pointer;
		}

		${PREFIX} ._thinkingToolRow._clickable:hover {
			background: ${t('surfaceColorTwo', '#f4f4f4')};
		}

		${PREFIX} ._thinkingToolIcon {
			font-size: 10px;
			flex-shrink: 0;
		}

		${PREFIX} ._thinkingToolEntry._running ._thinkingToolIcon {
			color: ${t('promptFontColor', '#1a1a1a')};
		}

		${PREFIX} ._thinkingToolEntry._success ._thinkingToolIcon {
			color: ${t('promptFontColor', '#1a1a1a')};
		}

		${PREFIX} ._thinkingToolEntry._error ._thinkingToolIcon {
			color: ${t('promptFontColor', '#1a1a1a')};
		}

		${PREFIX} ._thinkingToolName {
			font-weight: 500;
			color: ${t('promptFontColor', '#1a1a1a')};
		}

		${PREFIX} ._thinkingToolSummary {
			color: ${t('promptTertiaryFontColor', '#9b9b9b')};
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
			flex: 1;
			min-width: 0;
		}

		${PREFIX} ._thinkingToolToggle {
			font-size: 9px;
			color: ${t('promptTertiaryFontColor', '#9b9b9b')};
			flex-shrink: 0;
			margin-left: auto;
		}

		${PREFIX} ._thinkingReasoning {
			padding: 2px 12px;
			font-size: 12px;
			line-height: 1.6;
			color: ${t('promptTertiaryFontColor', '#8c8a80')};
			font-style: italic;
			margin: 0 8px 4px;
			white-space: pre-wrap;
			word-break: break-word;
			max-height: 400px;
			overflow-y: auto;
			border-left: 2px solid ${t('borderColorNine', '#e2e1db')};
		}

		${PREFIX} ._thinkingToolDetail {
			padding: 6px 12px 8px 26px;
			font-size: 12px;
			line-height: 1.5;
			color: ${t('promptSecondaryFontColor', '#555')};
			background: ${t('surfaceColorOne', '#f9f9f9')};
			border-radius: 0 0 6px 6px;
			margin: 0 8px 4px;
			white-space: pre-wrap;
			word-break: break-word;
		}

		/* ─── Monochrome status dot (running / success / error) ─── */
		${PREFIX} ._statusDot {
			display: inline-block;
			width: 8px;
			height: 8px;
			border-radius: 50%;
			flex-shrink: 0;
			box-sizing: border-box;
		}
		${PREFIX} ._statusDot._running {
			background: ${t('promptFontColor', '#1a1a1a')};
			animation: promptStatusDotPulse 1.2s ease-in-out infinite;
		}
		${PREFIX} ._statusDot._success {
			background: ${t('promptFontColor', '#1a1a1a')};
		}
		${PREFIX} ._statusDot._error {
			background: transparent;
			border: 1.5px solid ${t('promptFontColor', '#1a1a1a')};
		}
		${PREFIX} ._statusDot._sm {
			width: 6px;
			height: 6px;
		}
		@keyframes promptStatusDotPulse {
			0%, 100% { opacity: 1; transform: scale(1); }
			50%      { opacity: 0.25; transform: scale(0.75); }
		}

		${PREFIX} ._statusDotStatic {
			display: inline-block;
			width: 7px;
			height: 7px;
			border-radius: 50%;
			flex-shrink: 0;
			background: ${t('promptFontColor', '#1a1a1a')};
		}

		/* ─── Flat single-tool row (ThinkingBlock's lone tool, no wrapper) ─── */
		${PREFIX} ._agentGroupSingle {
			max-width: 768px;
			font-size: 13px;
			margin-bottom: 4px;
			align-self: flex-start;
		}

		/* ─── Agent activity card ─── */
		${PREFIX} ._agentCard {
			max-width: 768px;
			align-self: stretch;
			background: ${t('surfaceColorOne', '#fbfbf9')};
			border: 1px solid ${t('borderColorNine', '#ececec')};
			border-radius: 14px;
			overflow: hidden;
			margin-bottom: 4px;
			font-size: 13px;
		}

		${PREFIX} ._agentCard._done {
			background: ${t('backgroundColorOne', '#fff')};
		}

		${PREFIX} ._agentCardHead {
			display: flex;
			align-items: center;
			gap: 10px;
			padding: 10px 14px;
			border: none;
			background: none;
			font: inherit;
			color: inherit;
			width: 100%;
			text-align: left;
			cursor: pointer;
		}

		${PREFIX} ._agentCardHead:hover {
			background: ${t('surfaceColorTwo', '#f4f4f4')};
		}

		${PREFIX} ._agentCardSpin {
			width: 13px;
			height: 13px;
			border: 2px solid ${t('borderColorNine', '#e2e1db')};
			border-top-color: ${t('promptFontColor', '#1a1a1a')};
			border-radius: 50%;
			animation: promptAgentSpin 0.9s linear infinite;
			flex-shrink: 0;
		}

		@keyframes promptAgentSpin {
			to { transform: rotate(360deg); }
		}

		${PREFIX} ._agentCardCheck {
			width: 15px;
			height: 15px;
			border-radius: 50%;
			background: ${t('promptFontColor', '#1a1a1a')};
			color: ${t('backgroundColorOne', '#fff')};
			font-size: 9px;
			font-weight: 700;
			display: grid;
			place-items: center;
			flex-shrink: 0;
		}

		${PREFIX} ._agentCardTitle {
			font-weight: 600;
			color: ${t('promptFontColor', '#1a1a1a')};
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
		}

		${PREFIX} ._agentCardTime {
			margin-left: auto;
			font-size: 11px;
			color: ${t('promptTertiaryFontColor', '#8c8a80')};
			background: ${t('surfaceColorTwo', '#f4f3ee')};
			border: 1px solid ${t('borderColorNine', '#e6e5df')};
			border-radius: 999px;
			padding: 2px 8px;
			flex-shrink: 0;
		}

		${PREFIX} ._agentCardChevron {
			font-size: 10px;
			color: ${t('promptTertiaryFontColor', '#b6b4a9')};
			flex-shrink: 0;
		}

		${PREFIX} ._agentCardProgress {
			height: 3px;
			background: ${t('borderColorNine', '#f2f1ec')};
		}

		${PREFIX} ._agentCardProgress > div {
			height: 100%;
			background: ${t('promptFontColor', '#1a1a1a')};
			transition: width 0.5s ease;
		}

		${PREFIX} ._agentCardBody {
			border-top: 1px solid ${t('borderColorNine', '#f2f1ec')};
		}

		/* one agent inside the card */
		${PREFIX} ._agentItem {
			padding: 10px 14px 11px;
			border-bottom: 1px solid ${t('borderColorNine', '#f2f1ec')};
		}

		${PREFIX} ._agentItem:last-child {
			border-bottom: none;
		}

		${PREFIX} ._agentItemHead {
			display: flex;
			align-items: center;
			gap: 9px;
		}

		${PREFIX} ._agentItemName {
			font-weight: 600;
			color: ${t('promptFontColor', '#1a1a1a')};
		}

		${PREFIX} ._agentItemDur {
			margin-left: auto;
			font-size: 11px;
			color: ${t('promptTertiaryFontColor', '#b6b4a9')};
			white-space: nowrap;
		}

		/* live progress / final outcome line under the agent name */
		${PREFIX} ._agentDoing {
			margin: 3px 0 0 17px;
			font-size: 12.5px;
			color: ${t('promptSecondaryFontColor', '#5b5a53')};
		}

		${PREFIX} ._agentItem._success ._agentDoing,
		${PREFIX} ._agentItem._error ._agentDoing {
			color: ${t('promptTertiaryFontColor', '#8c8a80')};
		}

		${PREFIX} ._agentDoingCursor {
			display: inline-block;
			width: 6px;
			height: 11px;
			background: ${t('promptTertiaryFontColor', '#b6b4a9')};
			margin-left: 3px;
			vertical-align: -1px;
			animation: promptStatusDotPulse 0.9s steps(2) infinite;
		}

		/* sub-agent thinking: quiet quote, tail visible while streaming */
		${PREFIX} ._agentThink {
			margin: 7px 0 0 17px;
			border-left: 2px solid ${t('borderColorNine', '#e2e1db')};
			padding: 2px 10px;
			font-size: 12px;
			line-height: 1.5;
			color: ${t('promptTertiaryFontColor', '#8c8a80')};
			font-style: italic;
			max-height: 54px;
			overflow: hidden;
			cursor: pointer;
			white-space: pre-wrap;
			word-break: break-word;
		}

		${PREFIX} ._agentThink._expanded {
			max-height: 400px;
			overflow-y: auto;
		}

		${PREFIX} ._agentThink:hover {
			border-left-color: ${t('promptTertiaryFontColor', '#b6b4a9')};
		}

		/* tool lifecycle rows: appear on start, line swaps on update, settle on result */
		${PREFIX} ._agentCardTools {
			margin: 6px 0 0 17px;
			display: flex;
			flex-direction: column;
			gap: 2px;
		}

		${PREFIX} ._agentCardToolHead {
			display: flex;
			align-items: center;
			gap: 8px;
			padding: 3px 8px 3px 0;
			font-size: 12.5px;
			border: none;
			background: none;
			font: inherit;
			text-align: left;
			width: 100%;
			border-radius: 8px;
			color: ${t('promptSecondaryFontColor', '#666')};
		}

		${PREFIX} ._agentCardToolHead._clickable {
			cursor: pointer;
		}

		${PREFIX} ._agentCardToolHead._clickable:hover {
			background: ${t('surfaceColorTwo', '#f4f4f4')};
		}

		${PREFIX} ._agentCardToolName {
			color: ${t('promptFontColor', '#1a1a1a')};
			font-weight: 500;
		}

		${PREFIX} ._agentCardToolToggle {
			font-size: 10px;
			color: ${t('promptTertiaryFontColor', '#9b9b9b')};
			flex-shrink: 0;
			margin-left: auto;
		}

		${PREFIX} ._agentCardToolLive {
			color: ${t('promptSecondaryFontColor', '#5b5a53')};
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
			flex: 1;
			min-width: 0;
		}

		${PREFIX} ._agentCardTool._settled ._agentCardToolLive {
			color: ${t('promptTertiaryFontColor', '#8c8a80')};
		}

		${PREFIX} ._agentCardToolDur {
			margin-left: auto;
			font-size: 10.5px;
			color: ${t('promptTertiaryFontColor', '#b6b4a9')};
			white-space: nowrap;
			flex-shrink: 0;
		}

		${PREFIX} ._agentCardToolHist {
			margin: 2px 0 4px 22px;
			border-left: 1px solid ${t('borderColorNine', '#f2f1ec')};
			padding: 2px 0 2px 10px;
		}

		${PREFIX} ._agentCardToolHistLine {
			font-size: 11.5px;
			line-height: 1.6;
			color: ${t('promptTertiaryFontColor', '#b6b4a9')};
		}

		${PREFIX} ._agentCardToolHistFinal {
			font-size: 11.5px;
			line-height: 1.6;
			color: ${t('promptTertiaryFontColor', '#8c8a80')};
			white-space: pre-wrap;
			word-break: break-word;
		}

		${PREFIX} ._agentToolRow {
			display: flex;
			flex-direction: column;
		}

		${PREFIX} ._agentToolHeader {
			display: flex;
			align-items: center;
			gap: 8px;
			padding: 3px 8px;
			font-size: 12px;
			color: ${t('promptSecondaryFontColor', '#666')};
			border: none;
			background: none;
			font: inherit;
			text-align: left;
			width: 100%;
			border-radius: 6px;
		}

		${PREFIX} ._agentToolLabel {
			font-size: 12px;
			color: ${t('promptTertiaryFontColor', '#8b8b8b')};
		}

		${PREFIX} ._agentToolName {
			color: ${t('promptFontColor', '#1a1a1a')};
			font-weight: 500;
		}

		${PREFIX} ._agentToolSummary {
			color: ${t('promptTertiaryFontColor', '#9b9b9b')};
			flex: 1;
			min-width: 0;
			font-size: 12px;
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
		}

		${PREFIX} ._agentToolRow ._agentToolDetail {
			margin: 4px 0 4px 22px;
		}

		${PREFIX} ._agentToolHeader._clickable {
			cursor: pointer;
		}

		${PREFIX} ._agentToolHeader._clickable:hover ._agentToolName {
			color: ${t('promptFontColor', '#000')};
		}

		${PREFIX} ._agentToolToggle {
			font-size: 10px;
			color: ${t('promptTertiaryFontColor', '#9b9b9b')};
			flex-shrink: 0;
			margin-left: auto;
		}

		${PREFIX} ._agentToolDetail {
			padding: 6px 8px;
			font-size: 11px;
			line-height: 1.5;
			color: ${t('promptSecondaryFontColor', '#555')};
			background: ${t('surfaceColorTwo', '#f5f5f5')};
			border-radius: 6px;
			white-space: pre-wrap;
			word-break: break-word;
			max-height: 200px;
			overflow-y: auto;
		}

		/* A tool result is parsed markdown; its own blocks set the line breaks. */
		${PREFIX} ._toolSummaryMarkdown {
			white-space: normal;
		}

		${PREFIX} ._agentToolUpdates {
			padding: 2px 8px;
		}

		${PREFIX} ._agentToolUpdateLine {
			font-size: 11px;
			line-height: 1.6;
			color: ${t('promptTertiaryFontColor', '#9b9b9b')};
		}

		/* Streaming cursor */
		${PREFIX} ._streamingCursor {
			display: inline-block;
			width: 2px;
			height: 18px;
			background: ${t('promptFontColor', '#1a1a1a')};
			margin-left: 2px;
			vertical-align: text-bottom;
			animation: promptBlink 1s step-end infinite;
		}

		@keyframes promptBlink {
			0%, 100% { opacity: 1; }
			50% { opacity: 0; }
		}

		/* Message actions */
		${PREFIX} ._messageActions {
			display: flex;
			gap: 4px;
			margin-top: 8px;
		}

		${PREFIX} ._actionButton {
			width: 28px;
			height: 28px;
			border: none;
			background: transparent;
			color: ${t('promptTertiaryFontColor', '#9b9b9b')};
			cursor: pointer;
			border-radius: 6px;
			display: flex;
			align-items: center;
			justify-content: center;
			font-size: 13px;
			transition: background 0.15s, color 0.15s;
			padding: 0;
		}

		${PREFIX} ._actionButton:hover {
			background: ${t('surfaceColorTwo', '#f4f4f4')};
			color: ${t('promptFontColor', '#1a1a1a')};
		}

		${PREFIX} ._feedbackButton._active {
			color: ${t('promptFontColor', '#1a1a1a')};
			background: ${t('surfaceColorThree', '#e8e8e8')};
		}

		${PREFIX} ._feedbackButton._active:hover {
			background: ${t('surfaceColorThree', '#ddd')};
		}

		/* A settled reply's chips and copy buttons: one gentle entrance */
		${PREFIX} ._replySettled._entering {
			animation: promptSettleIn 0.22s ease-out;
		}

		@keyframes promptSettleIn {
			from { opacity: 0; transform: translateY(4px); }
			to { opacity: 1; transform: translateY(0); }
		}

		/* ─── Suggestion buttons ─── */
		${PREFIX} ._suggestions {
			display: flex;
			gap: 8px;
			flex-wrap: wrap;
			margin-top: 12px;
			max-width: 75%;
		}

		${PREFIX} ._suggestionButton {
			padding: 8px 16px;
			border: 1px solid ${t('borderColorTen', '#ddd')};
			border-radius: 18px;
			background: ${t('colorSeven', '#fff')};
			color: ${t('promptFontColor', '#1a1a1a')};
			font-size: 14px;
			cursor: pointer;
			transition: background 0.15s, border-color 0.15s;
		}

		${PREFIX} ._suggestionButton:hover {
			background: ${t('surfaceColorTwo', '#f4f4f4')};
			border-color: ${t('borderColorEleven', '#bbb')};
		}

		${PREFIX} ._suggestionButton:disabled {
			opacity: 0.5;
			cursor: not-allowed;
		}

		${PREFIX} ._suggestionButton._selected {
			background: ${t('surfaceColorThree', '#e8e8e8')};
			border-color: ${t('borderColorEleven', '#999')};
		}

		${PREFIX} ._suggestionsConfirm {
			background: ${t('promptFontColor', '#1a1a1a')};
			color: ${t('colorSeven', '#fff')};
			border-color: ${t('promptFontColor', '#1a1a1a')};
		}

		${PREFIX} ._suggestionsConfirm:hover {
			background: ${t('promptFontColor', '#333')};
		}

		/* ─── Craft Card (inline in chat) ─── */
		${PREFIX} ._craftCard {
			display: flex;
			align-items: center;
			gap: 10px;
			padding: 10px 14px;
			border: 1px solid ${t('borderColorNine', '#e5e5e5')};
			border-radius: 10px;
			background: ${t('surfaceColorOne', '#fafafa')};
			cursor: pointer;
			margin-top: 12px;
			width: fit-content;
			max-width: 300px;
			transition: background 0.15s, border-color 0.15s;
			font-family: inherit;
			text-align: left;
		}

		${PREFIX} ._craftCard:hover {
			background: ${t('surfaceColorTwo', '#f0f0f0')};
			border-color: ${t('borderColorTen', '#ccc')};
		}

		${PREFIX} ._craftCardIcon {
			font-size: 16px;
			color: ${t('promptSecondaryFontColor', '#666')};
			flex-shrink: 0;
		}

		${PREFIX} ._craftCardText {
			display: flex;
			flex-direction: column;
			gap: 2px;
			min-width: 0;
		}

		${PREFIX} ._craftCardTitle {
			font-size: 14px;
			font-weight: 500;
			white-space: nowrap;
			overflow: hidden;
			text-overflow: ellipsis;
		}

		${PREFIX} ._craftCardSubtitle {
			font-size: 12px;
			color: ${t('promptTertiaryFontColor', '#888')};
		}

		${PREFIX} ._craftCardChevron {
			font-size: 12px;
			color: ${t('fontColorTen', '#bbb')};
			flex-shrink: 0;
		}

		/* ─── Craft Cards Bar (above input) ─── */
		${PREFIX} ._craftCardsBar {
			display: flex;
			gap: 8px;
			padding: 8px 16px;
			overflow-x: auto;
			flex-shrink: 0;
			max-width: 768px;
			margin: 0 auto;
			width: 100%;
			box-sizing: border-box;
		}

		/* ─── Craft Panel (side panel) ─── */
		${PREFIX} ._promptMain._hasCraft {
			flex: 0 0 60%;
			max-width: 60%;
			min-width: 0;
		}

		${PREFIX} ._promptMain._hasCraft ._promptMessages {
			max-width: 100%;
		}

		${PREFIX} ._promptMain._hasCraft ._promptMessagesInner {
			max-width: 100%;
			padding: 0 16px;
		}

		${PREFIX} ._craftPanel {
			flex: 0 0 40%;
			max-width: 40%;
			border-left: 1px solid ${t('borderColorNine', '#e5e5e5')};
			display: flex;
			flex-direction: column;
			height: 100%;
			background: ${t('colorSeven', '#fff')};
			overflow: hidden;
			position: relative;
		}

		${PREFIX} ._craftJumpDown {
			position: absolute;
			bottom: 16px;
			left: 50%;
			transform: translateX(-50%);
			width: 32px;
			height: 32px;
			border-radius: 50%;
			background: ${t('colorSeven', '#fff')};
			border: 1px solid ${t('borderColorTen', '#ddd')};
			color: ${t('promptFontColor', '#333')};
			box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
			cursor: pointer;
			display: flex;
			align-items: center;
			justify-content: center;
			font-size: 12px;
			animation: _craftJumpIn 0.15s ease-out;
		}

		${PREFIX} ._craftJumpDown:hover {
			background: ${t('surfaceColorTwo', '#f5f5f5')};
		}

		@keyframes _craftJumpIn {
			from {
				opacity: 0;
				transform: translate(-50%, 4px);
			}
			to {
				opacity: 1;
				transform: translate(-50%, 0);
			}
		}

		${PREFIX} ._craftPanelHeader {
			display: flex;
			align-items: center;
			justify-content: space-between;
			padding: 14px 16px;
			border-bottom: 1px solid ${t('borderColorNine', '#e5e5e5')};
			flex-shrink: 0;
		}

		${PREFIX} ._craftPanelTitle {
			font-size: 16px;
			font-weight: 600;
		}

		${PREFIX} ._craftPanelClose {
			width: 28px;
			height: 28px;
			border: none;
			background: transparent;
			color: ${t('promptTertiaryFontColor', '#999')};
			cursor: pointer;
			border-radius: 6px;
			display: flex;
			align-items: center;
			justify-content: center;
			font-size: 14px;
		}

		${PREFIX} ._craftPanelClose:hover {
			background: ${t('surfaceColorTwo', '#f4f4f4')};
			color: ${t('promptFontColor', '#1a1a1a')};
		}

		${PREFIX} ._craftPanelBody {
			flex: 1;
			overflow-y: auto;
			padding: 16px;
		}

		/* ─── Craft Block Styles ─── */
		${PREFIX} ._craftContent {
			display: flex;
			flex-direction: column;
			gap: 12px;
		}

		${PREFIX} ._craftHeading {
			margin: 0;
		}

		${PREFIX} h1._craftHeading { font-size: 20px; }
		${PREFIX} h2._craftHeading { font-size: 16px; }
		${PREFIX} h3._craftHeading { font-size: 14px; }

		${PREFIX} ._craftText {
			margin: 0;
			font-size: 14px;
			line-height: 1.6;
			/* flush right edge — streaming rag doesn't dance */
			text-align: justify;
			hyphens: auto;
		}

		${PREFIX} ._craftBadge {
			display: inline-block;
			padding: 3px 10px;
			border-radius: 12px;
			font-size: 12px;
			font-weight: 500;
			background: ${t('surfaceColorTwo', '#f0f0f0')};
			color: ${t('promptSecondaryFontColor', '#555')};
			width: fit-content;
		}

		${PREFIX} ._craftKeyValue {
			display: flex;
			flex-direction: column;
			gap: 6px;
		}

		${PREFIX} ._craftKvRow {
			display: flex;
			gap: 8px;
			font-size: 14px;
		}

		${PREFIX} ._craftKvKey {
			color: ${t('promptSecondaryFontColor', '#666')};
			min-width: 100px;
			flex-shrink: 0;
		}

		${PREFIX} ._craftKvValue {
		}

		/* colorTwo is a SURFACE colour in the app themes (classic #F9FAFB, monoLight
		   #F4F4F2, monoDark #121211), never a text or accent colour: every rule below
		   that used it painted near-invisible text (ADZ-1225). A link is the text
		   colour, marked by its underline. */
		${PREFIX} ._craftKvValue a {
			color: ${t('promptFontColor', '#1967d2')};
			text-decoration: underline;
			text-underline-offset: 2px;
		}

		${PREFIX} ._craftKvValue a:hover {
			text-decoration: underline;
		}

		${PREFIX} ._craftImage a {
			display: block;
			cursor: zoom-in;
		}

		${PREFIX} ._craftImage img {
			max-width: 100%;
			border-radius: 8px;
			aspect-ratio: 16 / 10;
			object-fit: cover;
			background: ${t('surfaceColorTwo', '#f0f0f0')};
		}

		${PREFIX} ._craftImageCaption {
			font-size: 12px;
			color: ${t('promptTertiaryFontColor', '#888')};
			margin-top: 4px;
			display: block;
		}

		${PREFIX} ._craftTable {
			width: 100%;
			border-collapse: collapse;
			font-size: 13px;
		}

		${PREFIX} ._craftTable th,
		${PREFIX} ._craftTable td {
			padding: 8px 10px;
			text-align: left;
			border-bottom: 1px solid ${t('borderColorNine', '#eee')};
		}

		${PREFIX} ._craftTable th {
			font-weight: 600;
			color: ${t('promptSecondaryFontColor', '#666')};
			font-size: 12px;
			text-transform: uppercase;
		}

		${PREFIX} ._craftDivider {
			border: none;
			border-top: 1px solid ${t('borderColorNine', '#eee')};
			margin: 4px 0;
		}

		${PREFIX} ._craftMetric {
			display: flex;
			flex-direction: column;
			gap: 2px;
		}

		${PREFIX} ._craftMetricLabel {
			font-size: 12px;
			color: ${t('promptTertiaryFontColor', '#888')};
		}

		${PREFIX} ._craftMetricValue {
			font-size: 20px;
			font-weight: 600;
		}

		${PREFIX} ._craftMetricDetail {
			font-size: 12px;
			color: ${t('promptSecondaryFontColor', '#666')};
		}

		${PREFIX} ._craftMetricTrend._up { color: #34a853; }
		${PREFIX} ._craftMetricTrend._down { color: ${t('colorTwelve', '#ea4335')}; }

		${PREFIX} ._craftCallout {
			padding: 10px 14px;
			border-radius: 8px;
			font-size: 14px;
		}

		/* Text on a wash is the prompt's own font colour. colorTwo was used here, but in
		   every theme (classic, monoLight, monoDark) it is a SURFACE colour (#F9FAFB,
		   #F4F4F2, #121211), so callout text was all but invisible: the picture-less
		   creative cards of ADZ-1225 read as never-loading placeholders. */
		${PREFIX} ._craftCallout._info { background: ${t('infoWashColor', '#e8f0fe')}; color: ${t('promptFontColor', '#1967d2')}; }
		${PREFIX} ._craftCallout._warning { background: ${t('warningWashColor', '#fef7e0')}; color: ${t('promptFontColor', '#b06000')}; }
		${PREFIX} ._craftCallout._success { background: ${t('successWashColor', '#e6f4ea')}; color: ${t('promptFontColor', '#137333')}; }

		${PREFIX} ._craftList {
			margin: 0;
			padding-left: 20px;
			font-size: 14px;
			line-height: 1.8;
			color: ${t('promptFontColor', '#333')};
		}

		${PREFIX} ._craftRow {
			display: flex;
			gap: 16px;
			/* Children that can't shrink (fixed-size asset tiles) must scroll,
			   never spill past the panel edge (an 11-image assets_row rendered
			   half off-screen with no way to reach the tail, live 2026-09-16).
			   Rows whose children CAN flex (metric trios) never overflow, so
			   this stays invisible for them. Same thin-scrollbar treatment as
			   ._craftCarousel. */
			overflow-x: auto;
			scrollbar-width: thin;
			padding-bottom: 4px;
		}

		${PREFIX} ._craftRow > * {
			flex: 1;
		}

		/* a divider with nothing after it (section never filled) is noise */
		${PREFIX} ._craftContent > ._craftDivider:last-child {
			display: none;
		}

		/* Collapsible block — generic single-line summary + toggleable body.
		   Any agent can use it to surface optional detail without bloating
		   the craft canvas. */
		${PREFIX} ._craftCollapsible {
			border-top: 1px solid ${t('borderColorNine', '#eee')};
			border-bottom: 1px solid ${t('borderColorNine', '#eee')};
			margin: 4px 0;
		}

		${PREFIX} ._craftCollapsibleHeader {
			display: flex;
			align-items: center;
			gap: 6px;
		}

		${PREFIX} ._craftCollapsibleToggle {
			flex: 1;
			min-width: 0;
			display: flex;
			align-items: center;
			gap: 10px;
			padding: 10px 2px;
			background: none;
			border: none;
			cursor: pointer;
			font-size: 13px;
			color: ${t('promptSecondaryFontColor', '#555')};
			text-align: left;
		}

		${PREFIX} ._craftCollapsibleToggle:hover {
			color: ${t('promptFontColor', '#1a1a1a')};
		}

		${PREFIX} ._craftCollapsibleGlyph {
			font-size: 14px;
			color: ${t('promptTertiaryFontColor', '#888')};
			line-height: 1;
		}

		${PREFIX} ._craftCollapsibleSummary {
			flex: 1;
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
		}

		/* Badge ("4 ads") + website link stay visible while the card is closed. */
		${PREFIX} ._craftCollapsibleBadge {
			flex: none;
			font-size: 11px;
			line-height: 1;
			padding: 4px 8px;
			border-radius: 999px;
			background: #eef2ff;
			color: #4a5aa8;
			white-space: nowrap;
		}

		${PREFIX} ._craftCollapsibleLink {
			flex: none;
			padding: 6px 8px;
			font-size: 13px;
			line-height: 1;
			color: ${t('promptTertiaryFontColor', '#888')};
			text-decoration: none;
			border-radius: 6px;
		}

		${PREFIX} ._craftCollapsibleLink:hover {
			color: #1967d2;
			background: #f0f4ff;
		}

		${PREFIX} ._craftCollapsibleChevron {
			font-size: 16px;
			color: ${t('promptTertiaryFontColor', '#999')};
			line-height: 1;
			transition: transform 0.15s ease;
			display: inline-block;
		}

		${PREFIX} ._craftCollapsibleChevron._open {
			transform: rotate(90deg);
		}

		${PREFIX} ._craftCollapsibleBody {
			display: flex;
			flex-direction: column;
			gap: 12px;
			padding: 4px 2px 12px;
		}

		/* Horizontal creative carousel - assets-style thumbnail strip with
		   arrow paging when a competitor has many creatives. */
		${PREFIX} ._craftCarouselWrap {
			position: relative;
		}

		${PREFIX} ._craftCarousel {
			display: flex;
			gap: 10px;
			overflow-x: auto;
			padding-bottom: 6px;
			scroll-snap-type: x proximity;
			scrollbar-width: thin;
		}

		${PREFIX} ._craftCarousel > * {
			flex: 0 0 auto;
			width: 140px;
			scroll-snap-align: start;
		}

		${PREFIX} ._craftCarousel ._craftImage {
			position: relative;
		}

		${PREFIX} ._craftCarousel ._craftImage img {
			width: 140px;
			height: 175px;
			aspect-ratio: auto;
			object-fit: cover;
		}

		/* A card without a picture (a text ad, a creative with no image yet) is a
		   callout: about the picture's height, so it reads as a card with its text,
		   not as a pale strip. Not stretched to the row (flex-start): a stretched
		   -webkit-box shows the lines past its clamp under the ellipsis. */
		${PREFIX} ._craftCarousel > ._craftCallout {
			box-sizing: border-box;
			align-self: flex-start;
			overflow: hidden;
			font-size: 13px;
			line-height: 1.4;
			display: -webkit-box;
			-webkit-line-clamp: 8;
			-webkit-box-orient: vertical;
			/* the bottom space is a transparent border, not padding: overflow clips at
			   the padding edge, so a padding-bottom would show half of the next line */
			padding: 10px 12px 0;
			border-bottom: 10px solid transparent;
		}

		${PREFIX} ._craftCarousel ._craftImageCaption {
			overflow: hidden;
			text-overflow: ellipsis;
			display: -webkit-box;
			-webkit-line-clamp: 2;
			-webkit-box-orient: vertical;
		}

		${PREFIX} ._craftCarouselArrow {
			position: absolute;
			top: 78px;
			transform: translateY(-50%);
			z-index: 2;
			width: 28px;
			height: 28px;
			border-radius: 50%;
			border: 1px solid #e2e2e2;
			background: rgba(255, 255, 255, 0.95);
			box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
			color: ${t('promptSecondaryFontColor', '#444')};
			font-size: 18px;
			line-height: 1;
			cursor: pointer;
			display: flex;
			align-items: center;
			justify-content: center;
			padding: 0 0 2px;
		}

		${PREFIX} ._craftCarouselArrow:hover {
			background: #fff;
			color: ${t('promptFontColor', '#111')};
		}

		${PREFIX} ._craftCarouselArrow._left {
			left: -6px;
		}

		${PREFIX} ._craftCarouselArrow._right {
			right: -6px;
		}

		/* Status/days chips overlaid on the creative tile + metrics footer.
		   Zero-metric creatives carry no meta line at all (backend omits it). */
		${PREFIX} ._craftImageBadges {
			position: absolute;
			top: 6px;
			left: 6px;
			display: flex;
			gap: 4px;
			z-index: 1;
			pointer-events: none;
		}

		${PREFIX} ._craftImageBadge {
			font-size: 10px;
			line-height: 1;
			padding: 3px 6px;
			border-radius: 999px;
			background: rgba(0, 0, 0, 0.55);
			color: #fff;
			white-space: nowrap;
		}

		${PREFIX} ._craftImageBadge._active {
			background: rgba(22, 128, 61, 0.9);
		}

		${PREFIX} ._craftImageBadge._paused {
			background: rgba(90, 90, 90, 0.85);
		}

		${PREFIX} ._craftImageMeta {
			font-size: 11px;
			color: ${t('promptTertiaryFontColor', '#999')};
			margin-top: 2px;
			display: block;
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
		}

		/* Thumbnail variant of image block — fixed 48px square, contain (not
		   cover) so logos with transparent backgrounds render correctly.
		   Explicit flex override so a parent _craftRow flex-1 rule does
		   not stretch these. */
		${PREFIX} ._craftImage._thumbnail {
			display: inline-block;
			width: 48px;
			height: 48px;
			flex: 0 0 48px;
		}

		${PREFIX} ._craftImage._thumbnail a {
			display: block;
			width: 100%;
			height: 100%;
			border: 1px solid ${t('borderColorNine', '#e5e5e5')};
			border-radius: 8px;
			overflow: hidden;
			background: ${t('surfaceColorOne', '#fafafa')};
		}

		${PREFIX} ._craftImage._thumbnail img {
			width: 100%;
			height: 100%;
			object-fit: contain;
			aspect-ratio: auto;
			border-radius: 0;
			background: transparent;
		}

		/* Dark variant of the thumbnail tile — for white/transparent content
		   that would otherwise vanish on a light background. */
		${PREFIX} ._craftImage._thumbnail._dark a {
			background: ${t('promptFontColor', '#1a1a1a')};
			border-color: ${t('promptFontColor', '#333')};
		}

		/* Cover variant — for photo-style images that should fill the tile
		   without letterboxing. Use for creative/hero thumbnails. */
		${PREFIX} ._craftImage._thumbnail._cover img {
			object-fit: cover;
		}

		/* ─── Craft Panel responsive ─── */
		@media (max-width: 768px) {
			${PREFIX} ._craftPanel {
				position: fixed;
				top: 0;
				right: 0;
				bottom: 0;
				width: 100%;
				max-width: 100%;
				z-index: 100;
				border-left: none;
			}

			${PREFIX} ._promptMain._hasCraft {
				flex: 1;
				max-width: 100%;
			}
		}

		/* ─── Message attachments ─── */
		${PREFIX} ._messageAttachments {
			display: flex;
			gap: 8px;
			flex-wrap: wrap;
			align-self: flex-end;
			max-width: 75%;
		}

		${PREFIX} ._attachmentPreview {
			border-radius: 12px;
			overflow: hidden;
		}

		${PREFIX} ._attachmentImage {
			max-width: 200px;
			max-height: 150px;
			border-radius: 12px;
			object-fit: cover;
		}

		${PREFIX} ._attachmentFile {
			display: flex;
			align-items: center;
			gap: 6px;
			padding: 8px 12px;
			background: ${t('surfaceColorTwo', '#f4f4f4')};
			border-radius: 8px;
			font-size: 13px;
			color: ${t('promptFontColor', '#1a1a1a')};
		}

		/* An image that has not arrived yet. A secured attachment is fetched
		   with an auth header rather than served straight to the <img>, so
		   there is a real gap to fill; without a box of the right size the
		   whole transcript reflows when each one lands. */
		${PREFIX} ._attachmentImage._attachmentLoading {
			width: 120px;
			height: 90px;
			background: ${t('surfaceColorTwo', '#f4f4f4')};
		}

		/* A chat attachment is kept for ninety days and then deleted by the
		   retention job. This is what stands in its place afterwards — said
		   plainly, because a broken image icon reads as a bug rather than as
		   the documented behaviour it is. */
		${PREFIX} ._attachmentPreview._attachmentExpired ._attachmentFile {
			color: ${t('fontColorThree', '#9b9b9b')};
			font-style: italic;
		}

		/* ─── Saved-outright notice ───
		   Objects the user does not have open are written straight to the
		   database, unlike the ones on screen which wait for their Save. Nothing
		   else on the page distinguishes the two, so this says which is which. */
		${PREFIX} ._promptSavedNotice {
			display: flex;
			align-items: flex-start;
			gap: 8px;
			max-width: 768px;
			margin: 0 auto 8px;
			padding: 8px 12px;
			border-radius: 8px;
			font-size: 12px;
			line-height: 1.45;
			background-color: ${t('accentTintColor', 'rgba(245, 158, 11, 0.1)')};
			color: ${t('promptNoticeFontColor', '#92400e')};
		}

		${PREFIX} ._promptSavedNotice > span {
			flex: 1;
			min-width: 0;
			word-break: break-word;
		}

		${PREFIX} ._promptSavedDismiss {
			flex-shrink: 0;
			padding: 0;
			border: none;
			background: transparent;
			color: inherit;
			cursor: pointer;
			opacity: 0.7;
		}

		${PREFIX} ._promptSavedDismiss:hover {
			opacity: 1;
		}

		/* The review bar, for a chat with no editor around it. Same tint as the
		   notice above because it is the same news, but it stays until the work is
		   published or discarded rather than being dismissible: the point of it is
		   that unpublished work should not be easy to forget. */
		${PREFIX} ._promptPendingBar {
			max-width: 768px;
			margin: 0 auto 8px;
			padding: 10px 12px;
			border-radius: 8px;
			font-size: 12px;
			line-height: 1.45;
			background-color: ${t('accentTintColor', 'rgba(245, 158, 11, 0.1)')};
			color: ${t('promptNoticeFontColor', '#92400e')};
		}

		${PREFIX} ._promptPendingHead {
			display: flex;
			align-items: center;
			gap: 8px;
		}

		${PREFIX} ._promptPendingCount {
			flex: 1;
			min-width: 0;
		}

		${PREFIX} ._promptPendingList {
			margin: 4px 0 8px 22px;
			opacity: 0.85;
			word-break: break-word;
		}

		${PREFIX} ._promptPendingError {
			margin: 0 0 8px 22px;
			color: ${t('colorTwelve', '#dc3545')};
		}

		${PREFIX} ._promptPendingActions {
			display: flex;
			flex-wrap: wrap;
			align-items: center;
			gap: 6px;
			margin-left: 22px;
		}

		${PREFIX} ._promptPendingConfirmText {
			flex: 1 1 100%;
			margin-bottom: 4px;
		}

		${PREFIX} ._promptPendingBtn {
			display: inline-flex;
			align-items: center;
			gap: 5px;
			padding: 4px 9px;
			border-radius: 999px;
			border: 1px solid ${t('borderColorNine', 'rgba(146, 64, 14, 0.3)')};
			background: transparent;
			color: inherit;
			font: inherit;
			text-decoration: none;
			cursor: pointer;
			white-space: nowrap;
		}

		${PREFIX} ._promptPendingBtn:hover {
			background-color: ${t('accentTintColor', 'rgba(245, 158, 11, 0.18)')};
		}

		${PREFIX} ._promptPendingBtn:disabled {
			opacity: 0.55;
			cursor: default;
		}

		${PREFIX} ._promptPendingBtn._primary {
			border-color: transparent;
			background-color: ${t('promptFontColor', '#1a1a1a')};
			color: ${t('colorSeven', '#fff')};
		}

		${PREFIX} ._promptPendingBtn._danger {
			border-color: ${t('colorTwelve', '#dc3545')};
			color: ${t('colorTwelve', '#dc3545')};
		}

		/* ─── Input area ─── */
		${PREFIX} ._promptInputWrapper {
			padding: 0 16px 16px;
			transition: margin-top 0.3s ease;
		}

		${PREFIX} ._promptInputBar {
			max-width: 768px;
			margin: 0 auto;
			position: relative;
		}

		/* Input attachments preview */
		${PREFIX} ._inputAttachments {
			display: flex;
			gap: 8px;
			flex-wrap: wrap;
			padding: 8px 12px 0;
		}

		${PREFIX} ._inputAttachmentItem {
			position: relative;
			border-radius: 8px;
			overflow: hidden;
		}

		${PREFIX} ._inputAttachmentThumb {
			width: 64px;
			height: 64px;
			object-fit: cover;
			border-radius: 8px;
			display: block;
		}
		/* A file picked from the files service renders through AttachmentThumb inside the thumb. */
		${PREFIX} span._inputAttachmentThumb { overflow: hidden; }
		${PREFIX} span._inputAttachmentThumb ._attachmentPreview,
		${PREFIX} span._inputAttachmentThumb img {
			width: 100%;
			height: 100%;
			object-fit: cover;
			display: block;
		}

		${PREFIX} ._inputAttachmentFile {
			display: flex;
			align-items: center;
			gap: 6px;
			padding: 8px 12px;
			background: ${t('surfaceColorTwo', '#f4f4f4')};
			border-radius: 8px;
			font-size: 12px;
			color: ${t('promptFontColor', '#1a1a1a')};
			max-width: 150px;
		}

		${PREFIX} ._inputAttachmentFile span {
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
		}

		/* A video while it uploads: its name, then how far the upload is (ADZ-1326). */
		${PREFIX} ._inputAttachmentFile._uploading {
			max-width: 240px;
		}
		${PREFIX} ._inputAttachmentFile ._uploadProgress {
			flex-shrink: 0;
			opacity: 0.75;
		}

		/* Why a picked file was not attached (too large, upload failed). */
		${PREFIX} ._inputNotice {
			display: flex;
			align-items: flex-start;
			gap: 8px;
			margin: 8px 12px 0;
			padding: 8px 10px;
			border-radius: 8px;
			background: ${t('surfaceColorTwo', '#f4f4f4')};
			color: ${t('promptFontColor', '#1a1a1a')};
			font-size: 12px;
			line-height: 1.4;
		}
		${PREFIX} ._inputNotice span {
			flex: 1;
		}
		${PREFIX} ._inputNotice button {
			border: none;
			background: transparent;
			color: inherit;
			cursor: pointer;
			padding: 0;
			font-size: 11px;
		}

		${PREFIX} ._removeAttachment {
			position: absolute;
			top: 2px;
			right: 2px;
			width: 20px;
			height: 20px;
			border: none;
			border-radius: 50%;
			background: rgba(0, 0, 0, 0.6);
			color: ${t('colorSeven', '#fff')};
			cursor: pointer;
			display: flex;
			align-items: center;
			justify-content: center;
			font-size: 10px;
			padding: 0;
			transition: background 0.15s;
		}

		${PREFIX} ._removeAttachment:hover {
			background: rgba(0, 0, 0, 0.8);
		}

		${PREFIX} ._inputContainer {
			display: flex;
			align-items: flex-end;
			border: 1px solid ${t('borderColorNine', '#e5e5e5')};
			border-radius: 24px;
			background: ${t('surfaceColorTwo', '#f4f4f4')};
			padding: 8px 12px 8px 8px;
			transition: border-color 0.15s, box-shadow 0.15s;
		}

		${PREFIX} ._inputContainer:focus-within {
			border-color: ${t('borderColorTen', '#d0d0d0')};
			box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.04);
			background: ${t('colorSeven', '#fff')};
		}

		/* Plus button for attachments */
		${PREFIX} ._addAttachmentButton {
			width: 32px;
			height: 32px;
			border: 1px solid ${t('borderColorNine', '#e0e0e0')};
			border-radius: 50%;
			background: transparent;
			color: ${t('promptSecondaryFontColor', '#6b6b6b')};
			cursor: pointer;
			display: flex;
			align-items: center;
			justify-content: center;
			font-size: 14px;
			padding: 0;
			flex-shrink: 0;
			transition: background 0.15s, color 0.15s, border-color 0.15s;
		}

		${PREFIX} ._addAttachmentButton:hover {
			background: ${t('surfaceColorThree', '#ececec')};
			color: ${t('promptFontColor', '#1a1a1a')};
			border-color: ${t('borderColorTen', '#ccc')};
		}

		${PREFIX} ._addAttachmentButton:disabled {
			opacity: 0.4;
			cursor: not-allowed;
		}

		${PREFIX} ._inputContainer textarea {
			flex: 1;
			border: none;
			background: transparent;
			padding: 4px 8px;
			font-family: inherit;
			font-size: 15px;
			resize: none;
			outline: none;
			min-height: 24px;
			max-height: 200px;
			line-height: 1.5;
			/* InputBar switches this to auto once the box hits max-height. */
			overflow-y: hidden;
			color: ${t('promptFontColor', '#1a1a1a')};
		}

		${PREFIX} ._inputContainer textarea::placeholder {
			color: ${t('promptTertiaryFontColor', '#9b9b9b')};
		}

		${PREFIX} ._inputActions {
			display: flex;
			align-items: center;
			gap: 4px;
			margin-left: 4px;
			flex-shrink: 0;
		}

		${PREFIX} ._sendButton {
			width: 32px;
			height: 32px;
			border: none;
			border-radius: 50%;
			background: ${t('promptFontColor', '#1a1a1a')};
			color: ${t('colorSeven', '#fff')};
			cursor: pointer;
			display: flex;
			align-items: center;
			justify-content: center;
			font-size: 14px;
			transition: opacity 0.15s;
			padding: 0;
			position: relative;
		}

		${PREFIX} ._sendButton:hover {
			opacity: 0.8;
		}

		${PREFIX} ._sendButton:disabled {
			background: ${t('surfaceColorThree', '#d0d0d0')};
			cursor: not-allowed;
			opacity: 1;
		}

		/*
		 * Send, while a turn is running: it steers the run rather than starting
		 * one, and sits beside Stop rather than replacing it. Outlined, so the
		 * filled button next to it still reads as the one that ends the turn.
		 */
		${PREFIX} ._sendButton._steerButton {
			background: transparent;
			color: ${t('fontColorOne', '#1a1a1a')};
			border: 1px solid ${t('fontColorOne', '#1a1a1a')};
		}

		${PREFIX} ._stopButton {
			width: 32px;
			height: 32px;
			border: none;
			border-radius: 50%;
			background: ${t('promptFontColor', '#1a1a1a')};
			color: ${t('colorSeven', '#fff')};
			cursor: pointer;
			display: flex;
			align-items: center;
			justify-content: center;
			font-size: 12px;
			transition: opacity 0.15s;
			padding: 0;
		}

		${PREFIX} ._stopButton:hover {
			opacity: 0.8;
		}

		/* Microphone / voice input button */
		${PREFIX} ._micButton {
			width: 32px;
			height: 32px;
			border-radius: 50%;
			border: none;
			background: transparent;
			color: ${t('promptSecondaryFontColor', '#6b6b6b')};
			cursor: pointer;
			display: flex;
			align-items: center;
			justify-content: center;
			font-size: 14px;
			flex-shrink: 0;
			padding: 0;
			transition: background 0.2s, color 0.2s;
		}

		${PREFIX} ._micButton:hover {
			background: ${t('surfaceColorThree', '#ececec')};
			color: ${t('promptFontColor', '#1a1a1a')};
		}

		${PREFIX} ._micButton._recording {
			color: ${t('colorTwelve', '#ef4444')};
			background: ${t('errorWashColor', 'rgba(239, 68, 68, 0.1)')};
		}

		${PREFIX} ._micButton._recording:hover {
			background: rgba(239, 68, 68, 0.2);
		}

		${PREFIX} ._micButton:disabled {
			opacity: 0.4;
			cursor: not-allowed;
		}

		/* ─── Bottom bar ─── */
		${PREFIX} ._promptBottomBar {
			max-width: 768px;
			margin: 6px auto 0;
			display: flex;
			align-items: center;
			gap: 10px;
			padding: 0 4px;
		}

		/* ─── Model selector ─── */
		${PREFIX} ._modelSelector {
			position: relative;
			flex-shrink: 0;
		}

		${PREFIX} ._modelSelectorButton {
			display: flex;
			align-items: center;
			gap: 6px;
			border: none;
			background: transparent;
			color: ${t('promptTertiaryFontColor', '#9b9b9b')};
			font-size: 12px;
			font-family: inherit;
			cursor: pointer;
			padding: 4px 8px;
			border-radius: 6px;
			transition: background 0.15s, color 0.15s;
			white-space: nowrap;
		}

		${PREFIX} ._modelSelectorButton:hover {
			background: ${t('surfaceColorTwo', '#f4f4f4')};
			color: ${t('promptFontColor', '#1a1a1a')};
		}

		${PREFIX} ._modelSelectorButton:disabled {
			opacity: 0.5;
			cursor: not-allowed;
		}

		${PREFIX} ._modelSelectorChevron {
			font-size: 8px;
			opacity: 0.6;
		}

		${PREFIX} ._modelSelectorDropdown {
			position: absolute;
			bottom: calc(100% + 6px);
			left: 0;
			min-width: 220px;
			max-height: 320px;
			overflow-y: auto;
			background: ${t('colorSeven', '#fff')};
			border: 1px solid ${t('borderColorNine', '#e5e5e5')};
			border-radius: 12px;
			box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
			padding: 4px;
			z-index: 50;
			display: flex;
			flex-direction: column;
		}

		${PREFIX} ._modelSelectorOption {
			display: flex;
			align-items: center;
			justify-content: space-between;
			gap: 8px;
			width: 100%;
			border: none;
			background: transparent;
			color: ${t('promptFontColor', '#1a1a1a')};
			font-size: 13px;
			font-family: inherit;
			text-align: left;
			padding: 8px 12px;
			border-radius: 8px;
			cursor: pointer;
			transition: background 0.1s;
		}

		${PREFIX} ._modelSelectorOption:hover {
			background: ${t('surfaceColorTwo', '#f4f4f4')};
		}

		${PREFIX} ._modelSelectorOption._active {
			font-weight: 500;
		}

		${PREFIX} ._modelOptionName {
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
		}

		${PREFIX} ._modelOptionCheck {
			font-size: 11px;
			color: ${t('promptFontColor', '#1a1a1a')};
			flex-shrink: 0;
		}

		/* ─── Settings button ─── */
		${PREFIX} ._settingsButton {
			border: none;
			background: transparent;
			color: ${t('promptTertiaryFontColor', '#9b9b9b')};
			font-size: 12px;
			cursor: pointer;
			padding: 4px 6px;
			border-radius: 6px;
			transition: background 0.15s, color 0.15s;
		}

		${PREFIX} ._settingsButton:hover {
			background: ${t('surfaceColorTwo', '#f4f4f4')};
			color: ${t('promptFontColor', '#1a1a1a')};
		}

		/* ─── Usage bar ─── */
		${PREFIX} ._usageBar {
			display: flex;
			align-items: center;
			gap: 8px;
			font-size: 11px;
			color: ${t('promptTertiaryFontColor', '#9b9b9b')};
			margin-left: auto;
		}

		${PREFIX} ._usageSeparator {
			width: 3px;
			height: 3px;
			border-radius: 50%;
			background: ${t('surfaceColorThree', '#d0d0d0')};
			flex-shrink: 0;
		}

		${PREFIX} ._usageContext._warning {
			color: ${t('promptWarningFontColor', '#b45309')};
		}

		${PREFIX} ._usageContext._critical {
			color: ${t('colorTwelve', '#dc3545')};
			font-weight: 500;
		}

		${PREFIX} ._usageContextBar {
			width: 60px;
			height: 4px;
			background: ${t('surfaceColorThree', '#e5e5e5')};
			border-radius: 2px;
			overflow: hidden;
			flex-shrink: 0;
		}

		${PREFIX} ._usageContextFill {
			height: 100%;
			background: ${t('promptTertiaryFontColor', '#9b9b9b')};
			border-radius: 2px;
			transition: width 0.3s ease;
		}

		${PREFIX} ._usageContext._warning + ._usageContextBar ._usageContextFill {
			background: ${t('warningColor', '#b45309')};
		}

		${PREFIX} ._usageContext._critical + ._usageContextBar ._usageContextFill {
			background: ${t('colorTwelve', '#dc3545')};
		}

		/* ─── Confirmation Prompt ─── */
		${PREFIX} ._confirmationPrompt {
			margin: 12px 20px;
			padding: 16px;
			border: 1px solid ${t('borderColorNine', '#e0e0e0')};
			border-radius: 12px;
			background: ${t('surfaceColorOne', '#fafafa')};
			animation: confirmSlideIn 0.2s ease-out;
		}

		@keyframes confirmSlideIn {
			from { opacity: 0; transform: translateY(8px); }
			to { opacity: 1; transform: translateY(0); }
		}

		${PREFIX} ._confirmationHeader {
			display: flex;
			align-items: center;
			gap: 8px;
			margin-bottom: 8px;
		}

		${PREFIX} ._confirmationIcon {
			font-size: 16px;
			color: ${t('promptConfirmIconColor', t('warningColor', '#b45309'))};
		}

		${PREFIX} ._confirmationTitle {
			font-size: 14px;
			font-weight: 600;
			color: ${t('promptFontColor', '#1a1a1a')};
		}

		${PREFIX} ._confirmationMessage {
			font-size: 13px;
			color: ${t('promptSecondaryFontColor', '#444')};
			line-height: 1.5;
			margin: 0 0 12px;
			white-space: pre-wrap;
			word-break: break-word;
		}

		${PREFIX} ._confirmationOptions {
			display: flex;
			flex-wrap: wrap;
			gap: 8px;
		}

		${PREFIX} ._confirmationOption {
			padding: 7px 16px;
			border-radius: 8px;
			font-size: 13px;
			font-weight: 500;
			cursor: pointer;
			border: 1px solid ${t('borderColorTen', '#d0d0d0')};
			background: ${t('colorSeven', '#fff')};
			color: ${t('promptFontColor', '#1a1a1a')};
			transition: background 0.15s, border-color 0.15s;
		}

		${PREFIX} ._confirmationOption:hover {
			background: ${t('surfaceColorTwo', '#f0f0f0')};
			border-color: ${t('borderColorEleven', '#bbb')};
		}

		${PREFIX} ._confirmationOption._approve {
			background: #1a7f37;
			color: ${t('colorSeven', '#fff')};
			border-color: #1a7f37;
		}

		${PREFIX} ._confirmationOption._approve:hover {
			background: #15692e;
			border-color: #15692e;
		}

		${PREFIX} ._confirmationOption._deny {
			background: ${t('colorSeven', '#fff')};
			color: ${t('colorTwelve', '#dc3545')};
			border-color: ${t('colorTwelve', '#dc3545')};
		}

		${PREFIX} ._confirmationOption._deny:hover {
			background: ${t('errorWashColor', '#fef2f2')};
		}

		${PREFIX} ._confirmationOption._selected {
			background: ${t('infoWashColor', '#e8f0fe')};
			border-color: ${t('colorOne', '#1a73e8')};
			color: ${t('promptFontColor', '#1a73e8')};
		}

		${PREFIX} ._confirmationOption._selected:hover {
			background: ${t('infoWashColor', '#d2e3fc')};
		}

		${PREFIX} ._confirmationOption:disabled,
		${PREFIX} ._confirmationOption:disabled:hover {
			opacity: 0.45;
			cursor: not-allowed;
		}

		${PREFIX} ._confirmationPrompt._expired {
			animation: none;
		}

		${PREFIX} ._confirmationPrompt._expired ._confirmationIcon {
			color: ${t('promptSecondaryFontColor', '#555')};
		}

		${PREFIX} ._confirmationExpired {
			margin-left: auto;
			font-size: 12px;
			font-weight: 600;
			color: ${t('colorTwelve', '#dc3545')};
		}

		/* Resolved confirmation states */
		${PREFIX} ._confirmationPrompt._resolved {
			padding: 10px 16px;
			animation: none;
		}

		${PREFIX} ._confirmationPrompt._resolved ._confirmationHeader {
			margin-bottom: 0;
		}

		${PREFIX} ._confirmationPrompt._approved {
			background: ${t('successWashColor', '#f0fdf4')};
			border-color: ${t('successBorderColor', '#bbf7d0')};
		}

		${PREFIX} ._confirmationPrompt._approved ._confirmationIcon {
			color: #16a34a;
		}

		${PREFIX} ._confirmationPrompt._denied {
			background: ${t('errorWashColor', '#fef2f2')};
			border-color: ${t('errorBorderColor', '#fecaca')};
		}

		${PREFIX} ._confirmationPrompt._denied ._confirmationIcon {
			color: ${t('colorTwelve', '#dc3545')};
		}

		/* Details list */
		${PREFIX} ._confirmationDetails {
			font-size: 12px;
			color: ${t('promptSecondaryFontColor', '#555')};
			line-height: 1.5;
			margin: 0 0 12px;
			padding-left: 18px;
			list-style: disc;
		}

		${PREFIX} ._confirmationDetails li {
			margin-bottom: 2px;
		}

		/* ─── Map Block Styles (Added for Geotargeting UI) ─── */
		${PREFIX} ._craftMapBlock {
			border: 1px solid ${t('borderColorNine', '#e2e8f0')};
			border-radius: 12px;
			background: ${t('colorSeven', '#ffffff')};
			overflow: hidden;
			margin: 16px 0;
			box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.05), 0 2px 4px -2px rgb(0 0 0 / 0.05);
		}
		${PREFIX} ._mapSearchBox {
			position: relative;
			padding: 12px;
			border-bottom: 1px solid ${t('borderColorNine', '#e2e8f0')};
			background: ${t('surfaceColorOne', '#f8fafc')};
		}
		${PREFIX} ._mapSearchInput {
			width: 100%;
			padding: 8px 12px;
			border: 1px solid ${t('borderColorEleven', '#cbd5e1')};
			border-radius: 8px;
			font-size: 13px;
			color: ${t('promptFontColor', '#1e293b')};
			outline: none;
			transition: border-color 0.2s;
		}
		${PREFIX} ._mapSearchInput:focus {
			border-color: ${t('colorOne', '#6366f1')};
		}
		${PREFIX} ._mapSearchSpinner {
			position: absolute;
			right: 20px;
			top: 20px;
			font-size: 11px;
			color: ${t('promptTertiaryFontColor', '#64748b')};
		}
		${PREFIX} ._mapSuggestionsList {
			position: absolute;
			left: 12px;
			right: 12px;
			top: calc(100% - 4px);
			background: ${t('colorSeven', '#ffffff')};
			border: 1px solid ${t('borderColorNine', '#e2e8f0')};
			border-radius: 8px;
			box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1);
			max-height: 200px;
			overflow-y: auto;
			z-index: 10;
		}
		${PREFIX} ._mapSuggestionItem {
			width: 100%;
			display: flex;
			justify-content: space-between;
			align-items: center;
			padding: 8px 12px;
			border: none;
			background: ${t('colorSeven', '#ffffff')} !important;
			color: ${t('promptFontColor', '#1e293b')} !important;
			text-align: left;
			cursor: pointer;
			font-size: 13px;
			border-bottom: 1px solid ${t('borderColorNine', '#f1f5f9')};
		}
		${PREFIX} ._mapSuggestionItem:hover {
			background: ${t('surfaceColorTwo', '#f1f5f9')};
		}
		${PREFIX} ._mapSugName {
			font-weight: 500;
			color: ${t('promptFontColor', '#1e293b')};
		}
		${PREFIX} ._mapSugType {
			font-size: 11px;
			color: ${t('promptTertiaryFontColor', '#64748b')};
			background: ${t('surfaceColorTwo', '#f1f5f9')};
			padding: 2px 6px;
			border-radius: 4px;
		}
		${PREFIX} ._mapCanvasWrap {
			position: relative;
			height: 300px;
			background: ${t('surfaceColorTwo', '#f1f5f9')};
		}
		${PREFIX} ._mapCanvasDiv {
			width: 100%;
			height: 100%;
		}
		${PREFIX} ._mapCanvasOverlay {
			position: absolute;
			inset: 0;
			display: flex;
			align-items: center;
			justify-content: center;
			background: ${t('surfaceColorOne', 'rgba(248, 250, 252, 0.9)')};
			color: ${t('promptTertiaryFontColor', '#64748b')};
			font-size: 13px;
		}
		${PREFIX} ._mapTooltip {
			position: absolute;
			background: rgba(26, 26, 26, 0.95);
			color: ${t('colorSeven', '#fff')};
			padding: 8px 12px;
			border-radius: 6px;
			font-size: 11px;
			line-height: 1.6;
			pointer-events: none;
			z-index: 1000;
			box-shadow: 0 4px 12px rgba(0,0,0,0.15);
			white-space: nowrap;
			transition: opacity 0.1s ease;
		}

		/* ─── Map footer (detail panel) ─── */
		${PREFIX} ._mapFooter {
			padding: 12px;
			border-top: 1px solid ${t('borderColorNine', '#e2e8f0')};
			background: ${t('surfaceColorOne', '#fafafa')};
			min-height: 50px;
			display: flex;
			align-items: center;
		}
		${PREFIX} ._mapFooterDetail {
			display: flex;
			width: 100%;
			justify-content: space-between;
			align-items: center;
		}
		${PREFIX} ._mapFooterName {
			font-size: 13px;
			font-weight: 600;
			color: ${t('promptFontColor', '#1a1a1a')};
		}
		${PREFIX} ._mapFooterMeta {
			font-size: 11px;
			color: ${t('promptSecondaryFontColor', '#666')};
			margin-top: 2px;
		}
		${PREFIX} ._mapFooterDelete {
			border: none;
			background: none;
			color: ${t('colorFour', '#dc2626')};
			cursor: pointer;
			padding: 8px;
			font-size: 13px;
		}
		${PREFIX} ._mapFooterDelete:hover {
			color: ${t('colorTwelve', '#ef4444')};
		}
		${PREFIX} ._mapFooterHint {
			font-size: 11px;
			color: ${t('promptTertiaryFontColor', '#888')};
			font-style: italic;
			text-align: center;
			width: 100%;
		}

		/* Scroll to bottom button anchor — sits between messages and input,
		   zero height so it doesn't affect layout. Button positioned relative to this. */
		${PREFIX} ._scrollToBottomAnchor {
			position: relative;
			height: 0;
			z-index: 10;
			flex-shrink: 0;
		}

		${PREFIX} ._scrollToBottom {
			position: absolute;
			bottom: 12px;
			left: 50%;
			transform: translateX(-50%) translateY(0);
			width: 34px;
			height: 34px;
			border-radius: 50%;
			border: 0.5px solid ${t('borderColorNine', '#e5e5e5')};
			background: ${t('colorSeven', '#fff')};
			cursor: pointer;
			display: flex;
			align-items: center;
			justify-content: center;
			box-shadow: 0 1px 6px rgba(0,0,0,0.10);
			transition: opacity 0.25s ease, transform 0.25s ease, background 0.15s;
			opacity: 1;
			color: ${t('promptSecondaryFontColor', '#6b6b6b')};
		}

		${PREFIX} ._scrollToBottom._hidden {
			opacity: 0;
			transform: translateX(-50%) translateY(8px);
			pointer-events: none;
		}

		${PREFIX} ._scrollToBottom:hover:not(._hidden) {
			background: ${t('surfaceColorTwo', '#f4f4f4')};
			color: ${t('promptFontColor', '#1a1a1a')};
			transform: translateX(-50%) scale(1.05);
		}

		/* ─── LocationMap (inline location confirmation widget) ─── */
		${PREFIX} ._pLocationContainer {
			border: 1px solid ${t('borderColorNine', '#e5e5e5')};
			border-radius: 12px;
			overflow: hidden;
			margin: 8px 0;
		}
		${PREFIX} ._pLocationHeader {
			display: flex;
			align-items: center;
			gap: 8px;
			padding: 8px 14px;
			font-size: 13px;
			font-weight: 600;
			color: ${t('promptFontColor', '#1a1a1a')};
			background: ${t('surfaceColorOne', '#fafafa')};
		}
		${PREFIX} ._pLocationMapWrap {
			position: relative;
			width: 100%;
			height: 220px;
			background: ${t('surfaceColorTwo', '#f4f4f4')};
		}
		${PREFIX} ._pLocationMapDiv { width: 100%; height: 100%; }
		${PREFIX} ._pLocationMapOverlay {
			position: absolute;
			inset: 0;
			display: flex;
			align-items: center;
			justify-content: center;
			padding: 0 24px;
			text-align: center;
			color: ${t('promptSecondaryFontColor', '#666')};
			font-size: 13px;
			background: ${t('surfaceColorTwo', 'rgba(244, 244, 244, 0.85)')};
			pointer-events: none;
		}
		${PREFIX} ._pLocationFooter {
			display: flex;
			align-items: center;
			justify-content: space-between;
			gap: 12px;
			padding: 6px 14px;
			border-top: 1px solid ${t('borderColorNine', '#e5e5e5')};
			background: ${t('surfaceColorOne', '#fafafa')};
		}
		${PREFIX} ._pLocationHint { font-size: 12px; color: ${t('promptSecondaryFontColor', '#666')}; }
		${PREFIX} ._pLocationActions { display: flex; gap: 8px; }
		${PREFIX} ._pLocationConfirmBtn {
			padding: 5px 14px;
			border-radius: 8px;
			font-size: 12px;
			font-weight: 500;
			cursor: pointer;
			border: 1px solid transparent;
			background: ${t('promptFontColor', '#1a1a1a')};
			color: ${t('colorSeven', '#fff')};
		}
		${PREFIX} ._pLocationConfirmBtn:hover { opacity: 0.85; }
		${PREFIX} ._pLocationConfirmBtn:disabled { opacity: 0.5; cursor: not-allowed; }
		${PREFIX} ._pLocationConfirmedCard {
			display: flex;
			flex-direction: column;
			margin: 8px 0;
			width: 75%;
			max-width: 480px;
			border: 1px solid ${t('borderColorNine', '#e5e5e5')};
			border-radius: 10px;
			overflow: hidden;
			background: ${t('colorSeven', '#fff')};
		}
		${PREFIX} ._pLocationConfirmedMap {
			width: 100%;
			height: 240px;
			background: ${t('surfaceColorTwo', '#f4f4f4')};
			overflow: hidden;
		}
		${PREFIX} ._pLocationConfirmedMap img {
			width: 100%;
			height: 100%;
			object-fit: cover;
			display: block;
			filter: grayscale(100%);
		}
		${PREFIX} ._pLocationConfirmedRow {
			display: flex;
			align-items: center;
			gap: 8px;
			padding: 8px 12px;
			border-top: 1px solid ${t('borderColorNine', '#f0f0f0')};
			background: ${t('surfaceColorOne', '#fafafa')};
			min-height: 32px;
		}
		${PREFIX} ._pLocationConfirmedCheck {
			display: inline-flex;
			align-items: center;
			justify-content: center;
			width: 18px;
			height: 18px;
			flex: 0 0 18px;
			border-radius: 999px;
			background: ${t('promptFontColor', '#1a1a1a')};
			color: ${t('colorSeven', '#fff')};
		}
		${PREFIX} ._pLocationConfirmedAddr {
			flex: 1 1 auto;
			min-width: 0;
			font-size: 12px;
			font-weight: 500;
			color: ${t('promptFontColor', '#1a1a1a')};
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
		}
	` +
		ATTACH_SOURCE_MENU_CSS +
		processStyleDefinition(PREFIX, styleProperties, styleDefaults, theme);

	return <style id="PromptCss">{css}</style>;
}
